// src/pages/ColisCardsPublic.jsx
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  getDocs,
} from "firebase/firestore";

export default function ColisCardsPublic() {
  // --------- State principal ---------
  const [list, setList] = useState([]);
  // --- UI helpers (déclarations globales pour éviter jsx-no-undef) ---
function StatusBadge({ status }) {
  const map = {
    recu: { cls: "bg-success", label: "Reçu" },
    en_conteneur: { cls: "bg-primary", label: "En conteneur" },
    en_entrepot: { cls: "bg-warning text-dark", label: "À l'entrepôt" },
    en_attente: { cls: "bg-secondary", label: "En attente" },
  };
  const { cls, label } = map[status] || { cls: "bg-secondary", label: "—" };
  return <span className={`badge ${cls}`}>{label}</span>;
}

function PaidBadge({ est = 0, paid = 0 }) {
  if (!est && !paid) return <span className="badge bg-secondary">Pas d'estimation</span>;
  if (paid >= est) return <span className="badge bg-success">Soldé</span>;
  if (paid > 0) return <span className="badge bg-warning text-dark">Partiel</span>;
  return <span className="badge bg-secondary">Non payé</span>;
}

function PaidProgress({ est = 0, paid = 0 }) {
  const pct = est > 0 ? Math.min(100, Math.round((paid / est) * 100)) : 0;
  return (
    <div className="progress" style={{ height: 6 }}>
      <div className="progress-bar" style={{ width: `${pct}%` }} />
    </div>
  );
}

  // recherche & filtres (par défaut: À l'entrepôt)
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("en_entrepot"); // "all" | "en_attente" | "en_entrepot" | "en_conteneur" | "recu"

  // détails à la demande
  const [expandedId, setExpandedId] = useState(null);
  const [details, setDetails] = useState({}); // { [id]: { articles:[], lots:[] } }
  const [loadingDetails, setLoadingDetails] = useState(false);

  // galerie (images d’articles)
  const [showGallery, setShowGallery] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryTitle, setGalleryTitle] = useState("");
  const [loadingGallery, setLoadingGallery] = useState(false);

  // --------- Abonnement Firestore ---------
  useEffect(() => {
    const qy = query(collection(db, "colischine"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(qy, (snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setList(arr);
    });
    return () => unsub();
  }, []);

  // --------- Recherche & filtre ---------
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const arr = list.filter((c) => {
      const okStatus = statusFilter === "all" ? true : c.status === statusFilter;
      const inText = !s
        ? true
        : [c.code, c.clientName, c.clientPhone, c.origin, c.destination]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(s));
      return okStatus && inText;
    });

    // Quand filtre = "all", on met l'entrepôt en premier pour la lisibilité
    if (statusFilter === "all") {
      const weight = (st) =>
        st === "en_entrepot" ? 0 :
        st === "en_conteneur" ? 1 :
        st === "recu" ? 2 :
        st === "en_attente" ? 3 : 4;
      return [...arr].sort((a, b) => weight(a.status) - weight(b.status));
    }
    return arr;
  }, [list, search, statusFilter]);

  // --- Totaux basés sur la liste filtrée ---
  const totals = useMemo(() => {
    let sumEst = 0;
    let sumPaid = 0;
    for (const c of filtered) {
      sumEst += Number(c.estimateXOF || 0);
      sumPaid += Number(c.paidTotalXOF || 0);
    }
    return { sumEst, sumPaid, sumRest: sumEst - sumPaid };
  }, [filtered]);

  const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

  // --------- Détails: chargement lazy ---------
  const toggleDetails = async (colis) => {
    if (expandedId === colis.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(colis.id);
    if (details[colis.id]) return; // déjà en cache

    try {
      setLoadingDetails(true);
      const [artSnap, lotSnap] = await Promise.all([
        getDocs(collection(db, "colischine", colis.id, "articles")),
        getDocs(collection(db, "colischine", colis.id, "lots")),
      ]);
      const articles = artSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const lots = lotSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setDetails((prev) => ({ ...prev, [colis.id]: { articles, lots } }));
    } finally {
      setLoadingDetails(false);
    }
  };

  // --------- Galerie (images des articles) ---------
  const openGallery = async (colis) => {
    setShowGallery(true);
    setGalleryTitle(`${colis.code} — ${colis.clientName || ""}`);
    setLoadingGallery(true);
    try {
      let arts = details[colis.id]?.articles;
      if (!arts) {
        const artSnap = await getDocs(collection(db, "colischine", colis.id, "articles"));
        arts = artSnap.docs.map((d) => d.data());
        setDetails((prev) => ({
          ...prev,
          [colis.id]: { articles: arts, lots: prev[colis.id]?.lots || [] },
        }));
      }
      const imgs = (arts || []).flatMap((a) => (Array.isArray(a.images) ? a.images : []));
      setGalleryImages(imgs);
    } finally {
      setLoadingGallery(false);
    }
  };

  // --------- SEO (title + meta description) ---------
  useEffect(() => {
    // Title dynamique basé sur le nombre et le filtre
    const label = statusFilter === "all"
      ? "Tous"
      : statusFilter === "en_entrepot" ? "À l'entrepôt"
      : statusFilter === "en_conteneur" ? "En conteneur"
      : statusFilter === "recu" ? "Reçu"
      : "En attente";

    document.title = `Colis (public) — ${filtered.length} affichés • ${label}`;

    const desc = `Vue publique des colis Chine → Lomé : ${filtered.length} affichés. Total estimé ${fmt(totals.sumEst)} XOF, encaissé ${fmt(totals.sumPaid)} XOF, reste ${fmt(totals.sumRest)} XOF. Filtre: ${label}.`;

    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);
  }, [filtered.length, totals.sumEst, totals.sumPaid, totals.sumRest, statusFilter]);

  return (
    <div className="container py-4">
      {/* ===== Barre fixe (menu) ===== */}
      <div className="sticky-top bg-white border-bottom shadow-sm" style={{ zIndex: 1030, marginBottom: 24, }}>
        <div className="d-flex flex-wrap gap-2 align-items-end py-2">
          <div className="me-auto">
            <h3 className="mb-0 d-flex flex-wrap align-items-center gap-2">
              <span>📇 Colis (vue cartes)</span>
              <span className="badge bg-light text-dark">Total estimé: {fmt(totals.sumEst)} XOF</span>
              <span className="badge bg-success">Encaissé: {fmt(totals.sumPaid)} XOF</span>
              <span className="badge bg-secondary">Reste: {fmt(totals.sumRest)} XOF</span>
            </h3>
            <div className="text-muted small">
              Chine → Lomé • {filtered.length} colis
            </div>
          </div>

          <div>
            <label className="form-label small mb-1">Filtrer par statut</label>
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="en_entrepot">À l'entrepôt</option>
              <option value="en_conteneur">En conteneur</option>
              <option value="recu">Reçu</option>
              <option value="en_attente">En attente</option>
              <option value="all">Tous</option>
            </select>
          </div>

          <div style={{ minWidth: 260 }}>
            <label className="form-label small mb-1">Recherche</label>
            <input
              className="form-control form-control-sm"
              placeholder="Code, client, téléphone, origine…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>
      {/* ===== /Barre fixe ===== */}

      <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
        {filtered.map((c) => {
          const isOpen = expandedId === c.id;
          const det = details[c.id];

          const wh = (det?.lots || []).filter((l) => l.step === "warehouse").reduce((s, l) => s + Number(l.qty || 0), 0);
          const cont = (det?.lots || []).filter((l) => l.step === "container").reduce((s, l) => s + Number(l.qty || 0), 0);
          const lome = (det?.lots || []).filter((l) => l.step === "lome").reduce((s, l) => s + Number(l.qty || 0), 0);

          return (
            <div className="col" key={c.id}>
              <div className="card h-100 shadow-sm">
                <img
                  src={c.cover || "https://via.placeholder.com/640x360?text=Colis"}
                  className="card-img-top"
                  alt="cover"
                  style={{ objectFit: "contain", height: 260, cursor: "zoom-in" }}
                  onClick={() => openGallery(c)}
                />
                <div className="card-body d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h6 className="card-title mb-0">{c.code}</h6>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="text-muted small mb-1">
                    {c.clientName} • {c.clientPhone || "—"}
                  </div>
                  <div className="small mb-2">
                    {c.origin} → {c.destination}
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <PaidBadge est={Number(c.estimateXOF || 0)} paid={Number(c.paidTotalXOF || 0)} />
                    <div className="small">
                      {Number(c.paidTotalXOF || 0).toLocaleString("fr-FR")} / {Number(c.estimateXOF || 0).toLocaleString("fr-FR")} XOF
                    </div>
                  </div>
                  <PaidProgress est={Number(c.estimateXOF || 0)} paid={Number(c.paidTotalXOF || 0)} />

                  {/* Actions: lecture seule -> uniquement voir détails */}
                  <div className="mt-3 d-flex flex-wrap gap-2">
                    <button className="btn btn-sm btn-light ms-auto" onClick={() => toggleDetails(c)}>
                      {isOpen ? "Masquer détails" : "Voir détails"}
                    </button>
                  </div>

                  {/* Détails */}
                  {isOpen && (
                    <div className="mt-3 border-top pt-3">
                      {loadingDetails && !det && (
                        <div className="text-muted small">Chargement des détails…</div>
                      )}

                      {det && (
                        <>
                          {/* ARTICLES */}
                          <div className="table-responsive">
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th>Article</th>
                                  <th className="text-end">Qté</th>
                                  <th className="text-end">Poids/Taille</th>
                                  <th className="text-end">Valeur unitaire (XOF)</th>
                                  <th className="text-end">Valeur ligne (XOF)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {det.articles?.map((a) => {
                                  const unit = Number(a.unitValueXOF ?? a.value ?? 0);
                                  const qty = Number(a.qty || 0);
                                  const line = qty * unit;
                                  return (
                                    <tr key={a.id}>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          {Array.isArray(a.images) && a.images[0] && (
                                            <img src={a.images[0]} alt="" width={32} height={32} className="rounded border" style={{ objectFit: "cover" }} />
                                          )}
                                          <span>{a.title || "-"}</span>
                                        </div>
                                      </td>
                                      <td className="text-end">{qty}</td>
                                      <td className="text-end">{a.weight || "-"}</td>
                                      <td className="text-end">{unit.toLocaleString("fr-FR")}</td>
                                      <td className="text-end fw-semibold">{line.toLocaleString("fr-FR")}</td>
                                    </tr>
                                  );
                                })}
                                {(!det.articles || det.articles.length === 0) && (
                                  <tr>
                                    <td colSpan={5} className="text-center text-muted">Aucun article.</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* ÉTAPES + INFO CONTENEUR */}
                          <div className="small">
                            <span className="me-3">Étapes :</span>
                            <span className="me-3">Entrepôt (Chine) : <strong>{wh}</strong></span>
                            <span className="me-3">Conteneur : <strong>{cont}</strong></span>
                            <span>Réception à Lomé : <strong>{lome}</strong></span>
                          </div>

                          {(det?.lots || []).some((l) => l.step === "container" && (l.containerNo || l.shippingUrl)) && (
                            <div className="mt-2 small">
                              <div className="fw-semibold mb-1">Conteneurs :</div>
                              <ul className="mb-0">
                                {(det.lots || [])
                                  .filter((l) => l.step === "container" && (l.containerNo || l.shippingUrl))
                                  .map((l) => (
                                    <li key={l.id}>
                                      {l.containerNo && <span className="me-2">N° <strong>{l.containerNo}</strong></span>}
                                      {l.shippingUrl && (
                                        <a href={l.shippingUrl} target="_blank" rel="noopener noreferrer">
                                          Ouvrir le site de shipping
                                        </a>
                                      )}
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center text-muted py-5">Aucun colis pour ces critères.</div>
      )}

      {/* ======= MODALE GALERIE IMAGES D'ARTICLES ======= */}
      {showGallery && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ background: "rgba(0,0,0,.5)" }}>
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Images des articles — {galleryTitle}</h5>
                <button type="button" className="btn-close" onClick={() => setShowGallery(false)} />
              </div>
              <div className="modal-body">
                {loadingGallery ? (
                  <div className="text-muted">Chargement…</div>
                ) : galleryImages.length > 0 ? (
                  <div className="row g-2">
                    {galleryImages.map((u, i) => (
                      <div className="col-6 col-md-4 col-lg-3" key={i}>
                        <img
                          src={u}
                          alt={`article-${i}`}
                          className="img-fluid rounded border"
                          style={{ width: "100%", objectFit: "cover", aspectRatio: "1/1" }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">Aucune image d’article pour ce colis.</div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-light" onClick={() => setShowGallery(false)}>
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
